import type { TraxexSurveyEvent } from '@dmp/qqms/types';

/**
 * Fires a QQMS event to Traxex using the new /qqms-event POST endpoint.
 *
 * @param event - The TraxexSurveyEvent to send to Traxex (before server enrichment)
 * @param traxexApiUrl - Base URL for the Traxex API
 * @returns Promise that resolves when the event is successfully sent
 *
 * @throws Error if the Traxex API URL is not provided
 * @throws Error if the POST request fails
 *
 * Note: Traxex will enrich the event server-side by adding/overriding:
 * - event_timestamp (with server timestamp)
 * - geography (from Fastly headers when available, or preserved from client)
 * - user.user_agent (from HTTP headers)
 * - event_id (generated if not provided)
 */
export const fireTraxexEvent = async (
    event: TraxexSurveyEvent,
    traxexApiUrl: string
): Promise<void> => {
    // Check for duplicate events
    if (hasEventBeenFired(event)) {
        console.debug(
            '[QQMS] Event already fired, skipping:',
            event.event_type,
            event.event_id
        );
        return;
    }

    try {
        // Send POST request to /qqms-event endpoint
        const response = await fetch(`${traxexApiUrl}/qqms-event`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(event),
            // Use keepalive to ensure the request completes even if the page unloads
            keepalive: true,
        });

        if (!response.ok) {
            throw new Error(
                `Unexpected response: ${response.status} ${response.statusText}`
            );
        }

        // Mark event as successfully fired
        markEventAsFired(event);

        console.debug(
            '[QQMS] Event fired successfully:',
            event.event_type,
            event.event_id
        );
    } catch (error) {
        // Log error but don't throw - we don't want tracking failures to break the user experience
        console.error('[QQMS] Failed to fire event:', error);
    }
};

// ----------------------------------------------------------------
// Event deduplication tracking.
//
// Events are deduplicated based on a composite key of:
// - session_id
// - event_type
// - card id (if present)
//
// Once an event_type fires for a card within a given session,
// it will never fire again, regardless of time or changes in answers.
// ----------------------------------------------------------------

// In-memory storage for fired events
const firedEvents = new Set<string>();

/**
 * Generates a unique key for an event to track deduplication.
 */
const generateEventKey = (event: TraxexSurveyEvent): string => {
    return [event.session_id, event.event_type, event.card?.id]
        .filter(Boolean)
        .join('|');
};

/**
 * Checks if an event has already been fired.
 */
const hasEventBeenFired = (event: TraxexSurveyEvent): boolean => {
    const key = generateEventKey(event);
    return firedEvents.has(key);
};

/**
 * Marks an event as fired permanently.
 */
const markEventAsFired = (event: TraxexSurveyEvent): void => {
    const key = generateEventKey(event);
    firedEvents.add(key);
};

/**
 * Clears the fired events cache. Useful for testing.
 */
export const clearFiredEventsCache = (): void => {
    firedEvents.clear();
};
